import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { clearGeoServerTokenCookie } from '@/utils/geoserverAccess';
import { renewNow, usesUms } from '@/auth/umsSession';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

export const createApiClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: API_BASE_URL,
    timeout: 60000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor: Add JWT token
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const token = localStorage.getItem('auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error: AxiosError) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor: Handle 401 Unauthorized
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const original = error.config as (InternalAxiosRequestConfig & { _renewed?: boolean }) | undefined;
      if (error.response?.status === 401 && original && !original._renewed && (await usesUms())) {
        // A UMS token lives minutes: get a fresh one silently and try once more.
        original._renewed = true;
        if (await renewNow()) {
          original.headers.Authorization = `Bearer ${localStorage.getItem('auth_token')}`;
          return client(original);
        }
      }
      if (error.response?.status === 401) {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user_account');
        clearGeoServerTokenCookie();
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }
  );

  return client;
};

export const apiClient = createApiClient();
export default apiClient;
